<script setup lang="ts">
import { Image } from '@/components';
import { type FabricStore, getStyle3dFabricApi } from '@/api';
import { useRoute, useRouter } from 'vue-router';
import { useParamStore } from '@/store/param.ts';
import { storeToRefs, useRelationStore, useSelectionStore, useStyle3dStore } from '@repo/stores';
import { type FabricShareInfo, useStyle3d } from '@repo/style3d';
import { joinUrl } from '@repo/utils';
import axios, { type AxiosResponse } from 'axios';
import { generateFabricInfo, getAppConfig } from '@/utils';

const props = defineProps<{ fabrics: FabricStore[] }>();

const router = useRouter();
const route = useRoute();
const { refreshLoadResourceByRoute } = useParamStore();

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const paramStore = useParamStore();

const style3dStore = useStyle3dStore();
const { resolution } = storeToRefs(style3dStore);

const { OSS_BASE_URL, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

const { loadFabric } = useStyle3d();

const onClick = async (item: FabricStore) => {
  const { selectedFabricId } = selectionStore;
  const { query, params } = route;
  const { scoResource } = paramStore.resourceParam;
  // 兼容老数据，之前只存了id没有存fabricId
  const fabricId = item.fabricId || item.id;
  const originUrl = item.originUrl || '';
  
  console.log('[fabric-list] onClick:', {
    fabricId,
    selectedFabricId,
    currentParams: params,
    currentQuery: query
  });
  
  if (selectedFabricId === '') {
    await router.replace({
      params: { type: item.resourceType, id: fabricId },
      query: { ...query, originUrl: btoa(originUrl), color: undefined }
    });
    await refreshLoadResourceByRoute();
  } else {
    let fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>;
    if (item.resourceType === 'style3d') {
      const style3dFabric = await getStyle3dFabricApi(fabricId);
      const { xhr_path, thumb_path, width, height } = style3dFabric.data;
      const generateRibInfo = scoResource.type !== 'style3d';

      fabricShareInfo = await generateFabricInfo(
        {
          info: { width, height },
          thumb: thumb_path,
          xhr_path: xhr_path
        },
        generateRibInfo
      );
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      fabricShareInfo = infoResult.data;
    }

    const found = relationStore.shapeList.find(item => item.id === selectedFabricId);
    const materialIndex = found?.materialIndex ?? 0;
    
    console.log('[fabric-list] 更新面料:', {
      selectedFabricId,
      materialIndex,
      fabricId,
      foundShape: found
    });

    const filteredList = relationStore.shapeList.filter(item => {
      const itemMaterialIndex = item.materialIndex ?? 0;
      return itemMaterialIndex === materialIndex;
    });
    
    const updatedList = filteredList.map(item => {
      const fabricInfoType = item.type ? item.type : 'main';
      return {
        ...item,
        ...fabricShareInfo.info[fabricInfoType] ?? fabricShareInfo.info['main']
      };
    });

    const fabric = {
      id: item.id,
      type: item.resourceType,
      originUrl: item.originUrl
    };
    if (item.resourceType === 'style3d') {
      const shapes = await loadFabric(updatedList, { fabricRootPath: '', resolution: resolution.value, fabric });
      relationStore.updateShape(shapes);
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const shapes = await loadFabric(updatedList, {
        fabricRootPath: fabricRoot,
        resolution: resolution.value,
        fabric
      });
      relationStore.updateShape(shapes);
    }
    
    // 更新URL中对应materialIndex的面料ID
    const currentParams = params as any;
    
    // 构建新的路由参数，确保面料ID顺序正确
    const newParams = { ...currentParams };
    const newQuery: any = { ...query, originUrl: btoa(originUrl), color: undefined };
    
    console.log('[fabric-list] 更新路由前:', {
      materialIndex,
      fabricId,
      currentParams
    });
    
    // 根据materialIndex更新对应的参数位置，保持其他位置不变
    if (materialIndex === 0) {
      newParams.id = fabricId;
      newParams.type = item.resourceType;
      // 保持 id1 和 id2 不变
    } else if (materialIndex === 1) {
      newParams.id1 = fabricId;
      // 保持 id 和 id2 不变
    } else if (materialIndex === 2) {
      newParams.id2 = fabricId;
      // 保持 id 和 id1 不变
    }
    
    // 统一使用replace更新路由，避免竞态条件
    console.log('[fabric-list] 更新路由:', {
      materialIndex,
      fabricId,
      newParams,
      newQuery,
      '验证': {
        'id保持': materialIndex !== 0 ? newParams.id === currentParams.id : true,
        'id1保持': materialIndex !== 1 ? newParams.id1 === currentParams.id1 : true,
        'id2保持': materialIndex !== 2 ? newParams.id2 === currentParams.id2 : true
      }
    });
    
    await router.replace({
      params: newParams,
      query: newQuery
    });
  }
};
</script>

<template>
  <div
    style="padding: 0 5px;"
    v-for="(item) in props.fabrics"
    :key="item.id"
  >
    <Image
      :width="85"
      :height="85"
      :src="item.thumb"
      fit="scale-down"
      @click="()=>onClick(item)"
    />
    <div class="fabric-name">
      {{ item.info ? item.info.name : '' }}
    </div>
  </div>
</template>

<style scoped>
.fabric-name {
  color: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  text-align: center;
  margin-top: 6px;
  line-height: 1.2;
  max-height: 3.6em; /* allow up to 3 lines */
  width: 85px; /* match image width */
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: break-word;
  text-overflow: ellipsis;
  white-space: normal;
}
</style>